import intlTelInput from 'intl-tel-input';

function scrollToPartnership() {
    document.getElementById('partnership-section')?.scrollIntoView({ behavior: 'instant', block: 'start' });
}

async function showSuccessAlert(form) {
    if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
    }

    const Swal = (await import('sweetalert2/dist/sweetalert2.js')).default;
    await import('sweetalert2/src/sweetalert2.scss');

    Swal.fire({
        title: form.dataset.successTitle || '',
        text: form.dataset.successText || '',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#83927B',
    });
}

function loadRecaptcha(siteKey) {
    return new Promise((resolve, reject) => {
        if (window.grecaptcha) {
            resolve(window.grecaptcha);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.grecaptcha);
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

async function initRecaptcha(form) {
    const siteKey = form.dataset.recaptchaSitekey;
    const tokenInput = document.getElementById('recaptcha_v3_token');

    if (!siteKey || !tokenInput) {
        return;
    }

    const grecaptcha = await loadRecaptcha(siteKey);

    grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action: form.dataset.recaptchaAction || 'partnership_form' })
            .then(token => {
                tokenInput.value = token;
            });
    });
}

function initPartnershipForm() {
    const partnershipForm = document.getElementById('partnership_form');

    if (!partnershipForm) {
        return;
    }

    window.dataLayer = window.dataLayer || [];

    if (partnershipForm.dataset.scrollOnLoad === '1') {
        scrollToPartnership();
    }

    if (partnershipForm.dataset.success === '1') {
        showSuccessAlert(partnershipForm);
    }

    initRecaptcha(partnershipForm);

    const clearInvalid = (el) => {
        if (!el) return;
        el.classList.remove('is-invalid');
    };

    document.addEventListener('input', (event) => {
        const target = event.target;
        if (target.matches('input, textarea, select')) clearInvalid(target);
    }, true);

    document.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('input, textarea, select')) clearInvalid(target);
    }, true);

    const budgetInput = document.getElementById('budget');

    if (budgetInput) {
        const restrictBudgetToNumbers = (event) => {
            const inputElement = event.target;
            inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
            inputElement.classList.remove('is-invalid');
        };

        budgetInput.addEventListener('input', restrictBudgetToNumbers);
        budgetInput.addEventListener('blur', function () {
            restrictBudgetToNumbers({ target: this });
            this.classList.remove('is-invalid');
        });

        if (budgetInput.value) {
            restrictBudgetToNumbers({ target: budgetInput });
        }
    }

    const whatsappPhoneInput = document.getElementById('whatsapp_phone');
    const whatsappFullPhoneInput = document.getElementById('whatsapp_full_phone');

    if (!whatsappPhoneInput || !whatsappFullPhoneInput) {
        return;
    }

    try {
        const countryOptions = (() => {
            try {
                const parsed = JSON.parse(whatsappPhoneInput.dataset.countryOptions || '[]');

                return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
            } catch {
                return [];
            }
        })();

        const iti = intlTelInput(whatsappPhoneInput, {
            separateDialCode: true,
            initialCountry: whatsappPhoneInput.dataset.region || 'sg',
            countryOrder: countryOptions,
            countrySearch: false,
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
        });

        const validateWhatsappNumber = () => {
            const rawVal = whatsappPhoneInput.value.trim();
            if (rawVal === '') return true;

            const digitsOnly = rawVal.replace(/\D/g, '');
            const country = iti.getSelectedCountryData().iso2;

            if (country === 'sg') return digitsOnly.length === 8;
            if (country === 'my') return digitsOnly.length === 10 || digitsOnly.length === 11;
            if (country === 'us') return digitsOnly.length === 10;
            return digitsOnly.length >= 6 && digitsOnly.length <= 15;
        };

        const showWhatsappError = (message) => {
            const existingErrors = whatsappPhoneInput.parentNode.parentNode.querySelectorAll('.invalid-feedback');
            existingErrors.forEach(el => el.remove());

            const feedback = document.createElement('div');
            feedback.className = 'invalid-feedback d-block mt-1';
            feedback.textContent = message;

            const itiContainer = whatsappPhoneInput.parentNode;
            itiContainer.parentNode.insertBefore(feedback, itiContainer.nextSibling);
            whatsappPhoneInput.classList.add('is-invalid');
        };

        const clearWhatsappError = () => {
            const existingErrors = whatsappPhoneInput.parentNode.parentNode.querySelectorAll('.invalid-feedback');
            existingErrors.forEach(el => el.remove());
            whatsappPhoneInput.classList.remove('is-invalid');

            const itiInput = whatsappPhoneInput.parentNode.querySelector('.iti__input');
            itiInput?.classList.remove('is-invalid');
        };

        const restrictNonNumeric = (event) => {
            const inputElement = event.target;
            inputElement.value = inputElement.value.replace(/[^0-9]/g, '');

            if (validateWhatsappNumber()) {
                clearWhatsappError();
            }

            inputElement.classList.remove('is-invalid');
        };

        whatsappPhoneInput.addEventListener('input', restrictNonNumeric);
        whatsappPhoneInput.addEventListener('blur', function () {
            restrictNonNumeric({ target: this });
            this.classList.remove('is-invalid');

            if (this.value.trim() !== '' && validateWhatsappNumber()) {
                clearWhatsappError();
            }
        });

        whatsappPhoneInput.addEventListener('countrychange', function () {
            if (whatsappPhoneInput.value.trim() !== '' && validateWhatsappNumber()) {
                clearWhatsappError();
            }
        });

        if (whatsappPhoneInput.value) {
            restrictNonNumeric({ target: whatsappPhoneInput });

            if (validateWhatsappNumber()) {
                clearWhatsappError();
            }
        }

        const partnershipCountrySelect = document.getElementById('country_id');
        const cityInput = document.getElementById('city');

        partnershipCountrySelect?.addEventListener('change', function () {
            const selectedOption = this.options[this.selectedIndex];
            const countryCode = selectedOption?.dataset.countryCode;

            if (countryCode) {
                iti.setCountry(countryCode);

                if (cityInput) {
                    cityInput.placeholder = `e.g. ${selectedOption.text.trim()} City`;
                }

                if (whatsappPhoneInput.value.trim() !== '') {
                    setTimeout(() => {
                        if (validateWhatsappNumber()) {
                            clearWhatsappError();
                        } else {
                            showWhatsappError(partnershipForm.dataset.whatsappError || '');
                        }
                    }, 100);
                }

                return;
            }

            const countryText = this.options[this.selectedIndex].text.toLowerCase();

            if (countryText.includes('singapore') || countryText.includes('新加坡')) {
                iti.setCountry('sg');
                if (cityInput) cityInput.placeholder = 'e.g. Singapore City';
            } else if (countryText.includes('malaysia') || countryText.includes('马来西亚')) {
                iti.setCountry('my');
                if (cityInput) cityInput.placeholder = 'e.g. Malaysia City';
            }

            if (whatsappPhoneInput.value.trim() !== '') {
                setTimeout(() => {
                    if (validateWhatsappNumber()) {
                        clearWhatsappError();
                    } else {
                        showWhatsappError(partnershipForm.dataset.whatsappError || '');
                    }
                }, 100);
            }
        });

        if (partnershipCountrySelect?.value) {
            partnershipCountrySelect.dispatchEvent(new Event('change'));
        }

        partnershipForm.addEventListener('submit', function (event) {
            clearWhatsappError();

            if (!validateWhatsappNumber()) {
                event.preventDefault();
                showWhatsappError(partnershipForm.dataset.whatsappError || '');
                return;
            }

            whatsappFullPhoneInput.value = whatsappPhoneInput.value.trim() !== ''
                ? iti.getNumber() || whatsappPhoneInput.value
                : '';
        });
    } catch (error) {
        console.error('intl-tel-input init error:', error);
    }
}

document.addEventListener('DOMContentLoaded', initPartnershipForm);
